import BasicCard from "@components/ArticleCard/BasicCard";
import { CardVariations } from "@components/ArticleCard/types";
import { SanitizedSectionArticle } from "@typings/Cue";
import { cn, getUrlQuery } from "@util/helpers";

type LeadCardProps = {
  /** The article data to be displayed in the card */
  article: SanitizedSectionArticle;
  /** Optional className for the card */
  className?: string;
  /** Whether to display the blurb of the article */
  displayBlurb: boolean;
  /** Whether to display the edited timestamp of the article */
  displayEdited: boolean;
  /** Whether to display the updated timestamp of the article */
  displayUpdated: boolean;
  /** Whether to display the section of the article */
  displaySection: boolean;
  /** Optional className for the image */
  imageClassName?: string;
  /** Optional link parameters for the article */
  linkParams?: URLSearchParams;
  /** Indicator to disable paywall */
  disablePaywall?: boolean;
};

/**
 * LeadCard component to display a lead article with a large image and title.
 * It renders a BasicCard with specific variations for lead articles.
 */
const LeadCard = ({
  article,
  className,
  displayBlurb,
  displayEdited,
  displayUpdated,
  displaySection,
  imageClassName,
  linkParams,
  disablePaywall,
}: LeadCardProps): React.ReactElement => {
  const CardVariations: CardVariations = {
    content: {
      width: "w-full",
      extraClass: cn("mt-3 break-words"),
      blurb: {
        extraClass: cn("mb-1", { "max-lg:block": displayBlurb }),
      },
      updatedTimeColor: "text-gray-850",
    },
    title: {
      size: "text-4xl",
      color: "text-gray-850",
    },
    image: {
      position: "",
      width: cn("w-full", imageClassName),
    },
  };

  const params = getUrlQuery(article.urlPath, linkParams);

  return (
    <BasicCard
      id={article.id}
      title={article.title}
      kicker={article.kicker?.fields?.[0]?.value}
      media={article.media}
      slug={`${article.urlPath}${params}`}
      section={displaySection ? article.sections?.[0] : undefined}
      updated={displayUpdated ? article.updated : undefined}
      edited={displayEdited ? article.edited : undefined}
      hasUpdatedTimestampDisplay={
        article.displaySetting?.hasUpdatedTimeDisplayed
      }
      variations={CardVariations}
      blurb={displayBlurb ? article.blurb : undefined}
      rootClassName={className}
      paywall={!disablePaywall ? article.paywall?.contentAccess === "1" : false}
    />
  );
};

export default LeadCard;
